/**
 * StageRooms / StageRoom — 稽古場（RT-01 / Phase 3-B・B-1）
 *
 * 【B-1 のスコープ】稽古のみ。**観客席なし・録音なし**。
 *   録音まわりの文言は「この部屋は稽古用で、録音は残りません」に留める。
 *   B-3 で録音付きの本番上演を別に足すので、「ANOKOEでは録音しません」のような
 *   広い断定は書かない（将来嘘になる）。
 *
 * 【事実性①】在室者数は3値をそのまま出す（0＝いません／実数／null＝確認中）。
 * 【a11y】話者表示は色だけに頼らず名前側でも強調。接続状態と入退室は aria-live。
 *   演出は prefers-reduced-motion で静的代替に落とす（motion-safe: を使う）。
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import { api, StageRoom as StageRoomType } from '../services/api';
import { useStageRoom, StageConnState, StageRole } from '../hooks/useStageRoom';
import { toast } from '../hooks/use-toast';

const fmtWhen = (unixSec: number) =>
    new Date(unixSec * 1000).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** 演者の絶対上限。サーバー（StageRoomDO）の MAX_PERFORMERS と揃える。部屋ごとの実定員は performer_capacity */
const MAX_PERFORMERS = 4;
/** 部屋の演者定員（1〜4・0033以前の部屋は既定4）。⚠️Number(null)=0 のため欠落は先に分岐 */
const capacityOf = (r: StageRoomType) => {
    if (r.performer_capacity == null) return MAX_PERFORMERS;
    const n = Number(r.performer_capacity);
    return Number.isInteger(n) ? Math.min(MAX_PERFORMERS, Math.max(1, n)) : MAX_PERFORMERS;
};

/**
 * 3状態バッジ（2026-08-15 第2陣）: 募集中（演者に空きあり）/ 稽古中（演者満員）/ 終幕（閉室）。
 * 【事実性①】人数が確認中（null）のときは募集中/稽古中を**断定しない**（「確認中」を出す）。
 */
const RoomStatusBadge = ({ room }: { room: StageRoomType }) => {
    const base = 'text-[10px] font-bold px-2 py-0.5 rounded-full border';
    if (room.status === 'closed') {
        return <span className={`${base} bg-cream-2 text-ink-2 border-ink/30`}>終幕</span>;
    }
    if (room.performer_count === null) {
        return <span className={`${base} bg-cream-2 text-ink-2 border-ink/30`}>確認中</span>;
    }
    return room.performer_count >= capacityOf(room)
        ? <span className={`${base} bg-sun text-ink border-ink`}>稽古中</span>
        : <span className={`${base} bg-role-voice-soft text-role-voice-text border-role-voice`}>募集中</span>;
};

/**
 * 内訳を事実として1行にする。**3値の規則は演者・見学の両方に同じく当たる**。
 * どちらかが確認中なら「確認中」を混ぜず、その値だけを確認中と書く。
 */
const breakdownLabel = (performers: number | null, listeners: number | null, capacity?: number) => {
    // 定員が分かるときは「2/4人」形式（分母＝ホストが決めた演者定員）
    const p = performers === null ? '確認中' : capacity ? `${performers}/${capacity}人` : `${performers}人`;
    const l = listeners === null ? '確認中' : `${listeners}人`;
    return `演者 ${p}・見学 ${l}`;
};

// ============================================================
// 一覧
// ============================================================
export const StageRooms = () => {
    const [, navigate] = useLocation();
    const [rooms, setRooms] = useState<StageRoomType[]>([]);
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [title, setTitle] = useState('');
    /** 演者の定員（1〜4）。既定は4（従来と同じ） */
    const [capacity, setCapacity] = useState(4);

    const load = async () => {
        setLoading(true);
        const r = await api.stageListRooms();
        setRooms(r.rooms);
        setDisabled(r.disabled);
        setLoading(false);
    };
    useEffect(() => { void load(); }, []);

    const create = async () => {
        const t = title.trim();
        if (!t) { toast({ title: '稽古場の名前を入れてください', variant: 'destructive' }); return; }
        setCreating(true);
        const r = await api.stageCreateRoom(t, undefined, capacity);
        setCreating(false);
        if (!r.success || !r.id) {
            toast({ title: '稽古場を開けませんでした', description: r.code === 'FEATURE_DISABLED' ? 'いま準備中です' : r.error, variant: 'destructive' });
            return;
        }
        navigate(`/stage/rooms/${r.id}`);
    };

    return (
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-20">
            <header className="mb-8">
                <p className="font-display font-bold tracking-[0.18em] text-role-voice-text text-sm mb-2">REHEARSAL</p>
                <h1 className="font-display font-black text-4xl text-ink">稽古場</h1>
                <p className="text-ink-2 text-sm mt-3 leading-relaxed">
                    声を合わせて稽古する部屋です。演者の人数（1〜4人）は部屋を開くときに決められます。
                    <strong className="text-ink">ここは稽古用なので、録音は残りません。</strong>
                </p>
            </header>

            {disabled ? (
                // フラグOFF。開く導線を出さない（準備中であることを正直に伝える）
                <div className="bg-surface border-2 border-ink rounded-pop-md p-6 text-center">
                    <p className="text-ink font-display font-bold">稽古場はいま準備中です</p>
                    <p className="text-ink-2 text-sm mt-2">開けるようになったらお知らせします。</p>
                </div>
            ) : (
                <>
                    <section className="bg-surface border-2 border-ink rounded-pop-md p-5 mb-8 shadow-pop-sm">
                        <label className="block text-xs font-bold text-ink-2 mb-1" htmlFor="stage-title">新しい稽古場を開く</label>
                        <div className="flex gap-2 flex-wrap">
                            <input
                                id="stage-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80}
                                className="pop-field flex-1 min-w-[200px] min-h-[44px]" placeholder="例: 第一幕の読み合わせ"
                            />
                            <button onClick={create} disabled={creating} className="btn btn-primary min-h-[44px]">
                                {creating ? '開いています…' : '稽古場を開く'}
                            </button>
                        </div>
                        {/* 演者の定員（2026-08-15 第2陣）。定員に達すると以降は見学として入る */}
                        <div className="mt-3 flex items-center gap-2 flex-wrap" role="group" aria-label="演者の人数">
                            <span className="text-xs font-bold text-ink-2">演者の人数</span>
                            {[1, 2, 3, 4].map((n) => (
                                <button
                                    key={n}
                                    onClick={() => setCapacity(n)}
                                    aria-pressed={capacity === n}
                                    className={`btn btn-sm min-h-[44px] min-w-[52px] ${capacity === n ? 'btn-primary' : 'btn-ghost'}`}
                                >
                                    {n}人
                                </button>
                            ))}
                            <span className="text-[11px] text-ink-3">定員に達すると、それ以降は見学として入ります</span>
                        </div>
                        <p className="text-xs text-ink-2 mt-2">
                            イヤホンやヘッドホンの使用をおすすめします（スピーカーだと音が回ることがあります）。
                        </p>
                    </section>

                    <section aria-labelledby="open-rooms">
                        <h2 id="open-rooms" className="text-xs font-bold text-ink-2 mb-2">開いている稽古場</h2>
                        {loading ? (
                            <div className="space-y-2">
                                {[0, 1].map((i) => <div key={i} className="h-20 bg-cream-2 rounded-pop-sm motion-safe:animate-pulse" />)}
                            </div>
                        ) : rooms.length === 0 ? (
                            <p className="text-sm text-ink-2">
                                いま開いている稽古場はありません。最初のひと部屋を開けます。
                            </p>
                        ) : (
                            <ul className="space-y-2 list-none p-0 m-0">
                                {rooms.map((r) => (
                                    <li key={r.id}>
                                        <button
                                            onClick={() => navigate(`/stage/rooms/${r.id}`)}
                                            className="w-full text-left bg-surface border-2 border-ink/20 rounded-pop-sm p-4 min-h-[44px] hover:-translate-y-0.5 transition-transform"
                                        >
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-display font-bold text-ink">{r.title}</span>
                                                {/* 3状態（募集中/稽古中/終幕・確認中）を一目で（2026-08-15 第2陣） */}
                                                <RoomStatusBadge room={r} />
                                            </div>
                                            <div className="text-xs text-ink-2 mt-1 tabular-nums">
                                                {r.participant_count === 0
                                                    ? `いまは誰もいません（演者${capacityOf(r)}人まで）`
                                                    : breakdownLabel(r.performer_count, r.listener_count, capacityOf(r))}
                                                ・{fmtWhen(r.created_at)}に開室
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

// ============================================================
// 稽古場（本体）
// ============================================================
const STATE_LABEL: Record<StageConnState, string> = {
    connecting: 'つないでいます…',
    connected: 'つながりました',
    reconnecting: 'つなぎ直しています…',
    disconnected: 'つながりが切れました',
    error: 'つなげませんでした',
};

export const StageRoom = () => {
    const [, params] = useRoute('/stage/rooms/:id');
    const [, navigate] = useLocation();
    const roomId = params?.id ?? '';

    const [room, setRoom] = useState<StageRoomType | null>(null);
    const [roomDisabled, setRoomDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    /** 入室したか。**音声の自動再生はユーザー操作が要る**ので、入室ボタンを起点にする */
    const [entered, setEntered] = useState(false);
    /** 入室時に選んだ**希望**。確定はサーバー（満員なら見学者になる） */
    const [wantRole, setWantRole] = useState<StageRole>('performer');
    const [myId, setMyId] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            const r = await api.stageGetRoom(roomId);
            setRoom(r.room);
            setRoomDisabled(r.disabled);
            setLoading(false);
        })();
        api.getCurrentUser().then((r) => { if (r.success && r.data?.id) setMyId(r.data.id); });
    }, [roomId]);

    const stage = useStageRoom(roomId, entered, wantRole);

    if (loading) {
        return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-ink-2">読み込んでいます…</div>;
    }
    if (roomDisabled) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center">
                <h1 className="font-display font-black text-2xl text-ink mb-2">稽古場はいま準備中です</h1>
                <button className="btn btn-ghost min-h-[44px] mt-4" onClick={() => navigate('/stage')}>ちょっと戻る</button>
            </div>
        );
    }
    if (!room) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center">
                <h1 className="font-display font-black text-2xl text-ink mb-2">稽古場が見つかりません</h1>
                <p className="text-ink-2 text-sm">閉じられたか、URLが違うようです。</p>
                <button className="btn btn-ghost min-h-[44px] mt-4" onClick={() => navigate('/stage/rooms')}>稽古場の一覧へ</button>
            </div>
        );
    }
    // 終幕（閉室済み）。入口を出さず、状態を一目で伝える（2026-08-15 第2陣）
    if (room.status === 'closed') {
        return (
            <div className="max-w-3xl mx-auto px-6 py-20 text-center">
                <p className="mb-3"><RoomStatusBadge room={room} /></p>
                <h1 className="font-display font-black text-2xl text-ink mb-2">{room.title}</h1>
                <p className="text-ink-2 text-sm">この稽古場は終幕しました。録音は残っていません。</p>
                <button className="btn btn-primary min-h-[44px] mt-4" onClick={() => navigate('/stage/rooms')}>開いている稽古場を見る</button>
            </div>
        );
    }

    const isHost = !!myId && myId === room.host_id;
    /** この部屋の演者定員（1〜4・ホスト指定） */
    const roomCap = capacityOf(room);

    // ── 入室前の確認（どちらで入るか・マイク権限・ハウリング・録音しない事実） ──
    if (!entered) {
        // 演者が満員かどうかで入り方が変わる。**押す前に分かるように**先に事実を出す。
        // 確認中（null）のときは満員と決めつけない ─ 両方の入口を出しておく
        const performersFull = room.performer_count !== null && room.performer_count >= roomCap;
        const enter = (role: StageRole) => { setWantRole(role); setEntered(true); };

        return (
            <div className="max-w-2xl mx-auto px-6 pt-10 pb-20">
                <p className="font-display font-bold tracking-[0.18em] text-role-voice-text text-sm mb-2">REHEARSAL</p>
                <h1 className="font-display font-black text-3xl text-ink mb-2">{room.title}</h1>
                <p className="mb-4"><RoomStatusBadge room={room} /></p>

                <div className="bg-surface border-2 border-ink rounded-pop-md p-5 space-y-3 shadow-pop-sm">
                    <p className="text-ink text-sm leading-relaxed">
                        <strong>この部屋は稽古用です。録音は残りません。</strong>
                    </p>

                    <p className="text-ink-2 text-sm tabular-nums">
                        いまの様子: {room.participant_count === 0
                            ? `いまは誰もいません（先に入って待てます・演者${roomCap}人まで）`
                            : breakdownLabel(room.performer_count, room.listener_count, roomCap)}
                    </p>

                    {performersFull ? (
                        <>
                            {/* 断られた感じにしない。**可能性を先に出す** */}
                            <p className="text-ink text-sm leading-relaxed bg-role-voice-soft border-2 border-role-voice rounded-pop-sm px-3 py-2">
                                いま演者は{roomCap}名そろっています（稽古中）。<strong>見学で入れます。</strong>
                            </p>
                            <p className="text-ink-2 text-sm leading-relaxed">
                                見学ではマイクを使いません（権限も聞かれません）。聴きながら拍手を送れます。
                            </p>
                            <button className="btn btn-primary min-h-[44px] w-full" onClick={() => enter('listener')}>
                                見学で入る
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-ink-2 text-sm leading-relaxed">
                                演者で入るとマイクの許可を求められます。
                                <strong className="text-ink">イヤホンやヘッドホンの使用をおすすめします</strong>
                                （スピーカーだと音が回ることがあります）。
                                見学ではマイクを使いません。
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                <button className="btn btn-primary min-h-[44px] flex-1" onClick={() => enter('performer')}>
                                    演者として入る
                                </button>
                                <button className="btn btn-ghost min-h-[44px] flex-1" onClick={() => enter('listener')}>
                                    見学で入る
                                </button>
                            </div>
                        </>
                    )}

                    <button className="btn btn-ghost min-h-[44px] w-full" onClick={() => navigate('/stage/rooms')}>
                        ちょっと戻る
                    </button>
                </div>
            </div>
        );
    }

    const listening = stage.myRole === 'listener';
    /**
     * 「演者で入る」を押したのに満員で見学になったケース。
     * サーバーは満員時に**黙って**見学へ振り替える（拒否はしない）ため、
     * ここで事実を明示しないと「押したのに入れない」ロジック破綻に見える（2026-08-14 オーナー報告）。
     * 一覧の人数は最大2分古いので、押す前の画面では防ぎきれない＝入室後の通知が必須。
     */
    const demoted = listening && wantRole === 'performer';

    return (
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-20">
            <header className="mb-6">
                <p className="font-display font-bold tracking-[0.18em] text-role-voice-text text-sm mb-1">REHEARSAL</p>
                <h1 className="font-display font-black text-3xl text-ink">{room.title}</h1>
                <p className="text-ink-2 text-xs mt-1">この部屋は稽古用です。録音は残りません。</p>
                {listening && (
                    // 見学中であることを状態として示す（マイク操作は出さない）
                    <p className="inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full bg-role-voice-soft text-role-voice-text border-2 border-role-voice">
                        稽古を見学しています（声は出ません）
                    </p>
                )}
            </header>

            {/* 満員による見学への振り替え。黙って格下げしない — 何が起きたかを先に伝える */}
            {demoted && (
                <div role="status" className="bg-sun/30 border-2 border-ink rounded-pop-md p-4 mb-4">
                    <p className="text-sm text-ink font-bold">演者が満員だったため、見学として入室しました。</p>
                    <p className="text-xs text-ink-2 mt-1">
                        空きが出たら、いったん退出してから入り直すと演者で入れます（入室中の役割は変わりません）。
                    </p>
                </div>
            )}

            {/* 接続状態（5状態すべて出す・aria-liveで読み上げ） */}
            <div
                role="status" aria-live="polite"
                className="bg-cream-2 border-2 border-ink/15 rounded-pop-sm px-4 py-2 mb-4 flex items-center gap-2 flex-wrap"
            >
                <span
                    aria-hidden="true"
                    className={`inline-block w-2.5 h-2.5 rounded-full ${stage.state === 'connected' ? 'bg-role-voice' : stage.state === 'error' || stage.state === 'disconnected' ? 'bg-pop-dark' : 'bg-sun motion-safe:animate-pulse'}`}
                />
                <span className="text-sm text-ink font-bold">{STATE_LABEL[stage.state]}</span>
                {(stage.state === 'disconnected' || stage.state === 'error') && (
                    <button className="btn btn-ghost btn-sm min-h-[44px] ml-auto" onClick={stage.reconnect}>
                        もう一度つなぐ
                    </button>
                )}
            </div>

            {/* マイクが使えない（拒否・デバイス無し）。見学中はマイクを使わないので出さない */}
            {!listening && stage.micError && (
                <div role="alert" className="bg-surface border-2 border-pop-dark rounded-pop-md p-4 mb-4">
                    <p className="font-bold text-ink">マイクを使えませんでした</p>
                    <p className="text-sm text-ink-2 mt-1">
                        {stage.micError === 'denied'
                            ? 'ブラウザでマイクの使用が許可されていません。アドレスバーの設定から許可し直してください。'
                            : stage.micError === 'not-found'
                                ? 'マイクが見つかりませんでした。機器の接続を確認してください。'
                                : 'マイクの取得に失敗しました。'}
                    </p>
                    <button className="btn btn-ghost min-h-[44px] mt-3" onClick={() => void stage.retryMic()}>
                        もう一度ためす
                    </button>
                </div>
            )}

            {/* 自動再生がブロックされた（無音のまま原因不明にしない） */}
            {stage.audioBlocked && (
                <div role="alert" className="bg-sun/30 border-2 border-ink rounded-pop-md p-4 mb-4 flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-ink flex-1">ブラウザが自動再生を止めています。</p>
                    <button className="btn btn-primary min-h-[44px]" onClick={() => void stage.resumeAudio()}>音を出す</button>
                </div>
            )}

            {stage.error && (
                <p role="alert" className="text-sm text-pop-dark mb-4">{stage.error.message}</p>
            )}

            {/* 在室者 */}
            <section aria-labelledby="members" className="mb-6">
                <h2 id="members" className="text-xs font-bold text-ink-2 mb-2">
                    いま稽古している人（演者は最大{roomCap}名）
                </h2>
                <ul className="grid gap-2 sm:grid-cols-2 list-none p-0 m-0" aria-live="polite">
                    {stage.participants.length === 0 ? (
                        <li className="text-sm text-ink-2">いまは誰もいません（先に入って待てます）</li>
                    ) : stage.participants.map((p) => {
                        const talking = stage.speaking[p.userId];
                        const isMe = p.userId === stage.me?.userId;
                        const isListener = (p.role ?? 'performer') === 'listener';
                        return (
                            <li
                                key={p.userId}
                                className={`flex items-center gap-2 border-2 rounded-pop-sm px-3 py-2 ${talking ? 'border-role-voice bg-role-voice-soft' : 'border-ink/20 bg-surface'}`}
                            >
                                {/* 話者は色だけでなく、記号と太字でも示す（色覚に依存しない） */}
                                <span aria-hidden="true" className="text-xs w-4">{talking ? '🔊' : '　'}</span>
                                <span className={`text-sm text-ink truncate flex-1 ${talking ? 'font-bold' : ''}`}>
                                    {p.displayName}{isMe && '（あなた）'}
                                </span>
                                {isListener && (
                                    <span className="text-[10px] text-ink-2 border border-ink/20 rounded-full px-2 py-0.5 shrink-0">
                                        見学
                                    </span>
                                )}
                                {talking && <span className="sr-only">話しています</span>}
                            </li>
                        );
                    })}
                </ul>
            </section>

            {/* リモート音声。自動再生の失敗を検知して手動解除の導線を出す */}
            {stage.remotes.map((r) => (
                <RemoteAudioEl key={r.userId} stream={r.stream} onBlocked={() => stage.setAudioBlocked(true)} />
            ))}

            {/* 操作。見学中はマイクの操作を出さない（そもそも使っていない） */}
            <div className="flex gap-2 flex-wrap items-center">
                {listening ? (
                    <ClapButton onClap={stage.sendClap} clap={stage.clap} />
                ) : (
                    <button
                        onClick={stage.toggleMic}
                        aria-pressed={!stage.micOn}
                        className={`btn min-h-[44px] border-2 border-ink rounded-full px-5 ${stage.micOn ? 'bg-surface text-ink' : 'bg-ink text-cream'}`}
                    >
                        {stage.micOn ? 'マイクを切る' : 'マイクを入れる'}
                    </button>
                )}
                <button className="btn btn-ghost min-h-[44px]" onClick={() => { setEntered(false); navigate('/stage/rooms'); }}>
                    {listening ? '見学をやめる' : '稽古から出る'}
                </button>
                {isHost && (
                    <button
                        className="btn min-h-[44px] bg-pop-dark text-white border-2 border-ink rounded-full px-5 ml-auto"
                        onClick={async () => {
                            const r = await api.stageCloseRoom(roomId);
                            if (r.success) { toast({ title: '稽古場を閉じました' }); navigate('/stage/rooms'); }
                            else toast({ title: '閉じられませんでした', description: r.error, variant: 'destructive' });
                        }}
                    >
                        稽古場を閉じる
                    </button>
                )}
            </div>

            {!listening && !stage.micOn && (
                // ミュートの事実だけを書く。「通信量を節約」とは書かない（B-0 実測で帯域は減らない）
                <p className="text-xs text-ink-2 mt-3">いまマイクは切れています。あなたの声は相手に聞こえません。</p>
            )}
            {listening && (
                <p className="text-xs text-ink-2 mt-3">
                    見学中はマイクを使いません。拍手だけ届けられます。
                </p>
            )}

            {stage.closedReason && (
                <p className="text-sm text-ink-2 mt-4" role="status">
                    この稽古場は閉じました。
                    <button className="underline ml-1" onClick={() => navigate('/stage/rooms')}>一覧へ戻る</button>
                </p>
            )}
        </div>
    );
};

/**
 * 拍手。サーバーが2秒の窓で集計した**実際の回数**が届く。
 *
 * 【`aria-live` を付けない】2秒ごとに読み上げられると操作の妨げになる。
 * 拍手は装飾的な情報として扱い、参加者の増減・接続状態の通知（そちらは aria-live あり）とは分ける。
 * 表示も控えめにする — 「いま拍手が届いている」が伝わればよく、数の追跡が目的ではない。
 *
 * 【reduced-motion】アニメーションは motion-safe: に閉じ、静的な文字表示に落ちる。
 */
const ClapButton = ({ onClap, clap }: { onClap: () => boolean; clap: { count: number; at: number } | null }) => {
    const [cooling, setCooling] = useState(false);
    const [recent, setRecent] = useState<{ count: number; at: number } | null>(null);

    // 届いた拍手は数秒だけ出す（出しっぱなしにしない）
    useEffect(() => {
        if (!clap) return;
        setRecent(clap);
        const id = window.setTimeout(() => setRecent(null), 2500);
        return () => window.clearTimeout(id);
    }, [clap]);

    const handle = () => {
        if (!onClap()) return;          // 連打はフックが弾く（サーバー側でも捨てられる）
        setCooling(true);
        window.setTimeout(() => setCooling(false), 2000);
    };

    return (
        <div className="flex items-center gap-2">
            <button
                onClick={handle}
                disabled={cooling}
                className="btn min-h-[44px] border-2 border-ink rounded-full px-5 bg-sun text-ink disabled:opacity-50"
            >
                <span aria-hidden="true">👏</span> 拍手を送る
            </button>
            {recent && (
                // aria-hidden: 読み上げから外す（2秒ごとの通知は妨げになる）
                <span
                    aria-hidden="true"
                    className="text-sm text-ink-2 tabular-nums motion-safe:animate-pulse"
                >
                    拍手 {recent.count}
                </span>
            )}
        </div>
    );
};

/**
 * リモート音声の再生。**自動再生はユーザー操作なしにはブロックされる**ので、
 * play() の失敗を検知して手動解除の導線を親に伝える（無音のまま放置しない）。
 */
const RemoteAudioEl = ({ stream, onBlocked }: { stream: MediaStream; onBlocked: () => void }) => {
    const ref = useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.srcObject = stream;
        el.play().catch(() => onBlocked());
        return () => { el.srcObject = null; };
    }, [stream, onBlocked]);
    return <audio ref={ref} autoPlay playsInline className="hidden" />;
};
